/**
 * `entities/brief` query options factory.
 *
 * `briefsQueryOptions` is a thin TanStack `queryOptions` factory wrapping
 * `ProjectBriefsService.list` from the workspace SDK. Consumers compose it
 * with whichever observer fits the call site: `useQuery`, `useSuspenseQuery`,
 * `queryClient.prefetchQuery`, route loaders, etc.
 *
 * Design notes:
 *   - Server-side everything: search, pagination, and (future) sort live in
 *     the query params; no client-side filtering.
 *   - `placeholderData: keepPreviousData` keeps the previous page rendered
 *     while a page/search transition is in-flight.
 *   - Optional query params (`search`, `sortBy`, `sortOrder`) are conditionally
 *     spread so they're absent — not `undefined` — on the wire.
 *   - `enabled` is set to `Boolean(projectId)`, so consumers can call the
 *     factory eagerly with a `params.projectId` string from a route param
 *     without an extra guard at the call site.
 *   - `workspaceClient.setConfig({ throwOnError: true })` is set globally in
 *     `lib/api/workspace-client.ts`, so HTTP errors reject the underlying
 *     promise. The `if (!response.data)` check is a defensive guard for the
 *     pathological 200-with-empty-body case (mirrors `entities/project-folder`).
 */
import {
  ProjectBriefsService,
  TrackersService,
  type ListTrackerRowsResponseDto,
  type TrackerRowDto,
} from '@inference.engine/workspace-api-client/web';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { workspaceHttp } from '@/lib/api/http-client';
import { BRIEF_TYPE_LABEL } from '../model/constants';
import type { Brief, BriefSortField, BriefSortOrder, BriefStatus, BriefType } from '../model/types';
import { BRIEFS_QUERY_KEYS } from './query-keys';

/**
 * Inputs accepted by {@link briefsQueryOptions}.
 *
 * `status` is a multi-value filter — the endpoint accepts `status[]`. The
 * list-view toolbar drives it single-select today, but the param stays an
 * array so a future multi-select UI needs no signature change. Kept in sync
 * with `BriefsListKeyParams`.
 */
export interface BriefsListParams {
  projectId: string;
  page: number;
  pageSize: number;
  search?: string;
  status?: BriefStatus[];
  sortBy?: BriefSortField;
  sortOrder?: BriefSortOrder;
}

export const briefsQueryOptions = (params: BriefsListParams) =>
  queryOptions({
    queryKey: BRIEFS_QUERY_KEYS.list(params),
    queryFn: async ({ signal }) => {
      const response = await ProjectBriefsService.list({
        client: workspaceHttp,
        path: { platformProjectId: params.projectId },
        query: {
          page: params.page,
          pageSize: params.pageSize,
          ...(params.search ? { search: params.search } : {}),
          ...(params.status?.length ? { status: params.status } : {}),
          ...(params.sortBy ? { sortBy: params.sortBy } : {}),
          ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
        },
        signal,
      });

      if (!response.data) {
        throw new Error('Failed to list briefs');
      }

      return response.data;
    },
    placeholderData: keepPreviousData,
    enabled: Boolean(params.projectId),
  });

/**
 * Inputs accepted by {@link briefByIdQueryOptions}.
 *
 * `enabled` is gated on BOTH `projectId` and `briefId` so the factory can be
 * called eagerly with route params (without a guard at the call site).
 */
export interface BriefByIdParams {
  projectId: string;
  briefId: string;
}

/**
 * Single-brief query options factory. Wraps `ProjectBriefsService.getById`
 * and uses `BRIEFS_QUERY_KEYS.detail` for cache identity. Mirrors
 * {@link briefsQueryOptions} including the defensive `if (!response.data)`
 * guard for the pathological 200-with-empty-body case.
 */
export const briefByIdQueryOptions = (params: BriefByIdParams) =>
  queryOptions({
    queryKey: BRIEFS_QUERY_KEYS.detail(params.projectId, params.briefId),
    queryFn: async ({ signal }) => {
      const response = await ProjectBriefsService.getById({
        client: workspaceHttp,
        path: { platformProjectId: params.projectId, briefId: params.briefId },
        signal,
      });

      if (!response.data) {
        throw new Error('Failed to load brief');
      }

      return response.data;
    },
    enabled: Boolean(params.projectId && params.briefId),
  });

export interface ProjectBriefsGroup {
  /**
   * The workspace-api INTERNAL project id: the key the board groups and
   * persists layout by. NOT the platform id used to address the route.
   */
  projectId: string;
  /** The platform id, which is what a brief's `projects/:platformProjectId/*` route takes. */
  platformProjectId: string;
  projectTitle: string | null;
  briefs: Brief[];
}

export interface BoardBriefsParams {
  workspaceId: string;
  trackerId: string;
}

type BoardUser = ListTrackerRowsResponseDto['users'][number];

const BOARD_BRIEFS_PAGE_SIZE = 200;

function textField(fields: TrackerRowDto['fields'], name: string): string {
  const value = fields[name];
  return typeof value === 'string' ? value : '';
}

function briefStatus(value: unknown): BriefStatus {
  return value === 'submitted' ? 'submitted' : 'draft';
}

function isBriefType(value: unknown): value is BriefType {
  return typeof value === 'string' && Object.hasOwn(BRIEF_TYPE_LABEL, value);
}

function briefType(value: unknown): BriefType | undefined {
  return isBriefType(value) ? value : undefined;
}

/**
 * A row carries only what the board shows, so the rest of the brief (sections,
 * sources, summary) is explicitly empty: the brief detail page loads its own.
 */
function briefFromRow(row: TrackerRowDto, userById: Map<string, BoardUser>): Brief {
  const { fields } = row;
  const createdBy = textField(fields, 'createdBy');
  const ownerId = textField(fields, 'ownerId') || null;
  const userFor = (id: string) => {
    const user = userById.get(id);
    return { id, name: user?.name ?? '', avatarUrl: user?.avatarUrl ?? null };
  };
  return {
    id: row.rowKey,
    projectId: textField(fields, 'projectId'),
    // eslint-disable-next-line i18next/no-literal-string -- row field name, not copy
    title: textField(fields, 'title'),
    briefType: briefType(fields.briefType),
    status: briefStatus(fields.status),
    customColumnValues: row.customColumnValues,
    customColumnMeta: row.customColumnMeta ?? null,
    createdBy,
    createdByUser: userFor(createdBy),
    ownerId,
    owner: userFor(ownerId ?? createdBy),
    createdAt: textField(fields, 'createdAt'),
    updatedAt: textField(fields, 'updatedAt'),
    sections: [],
    sources: [],
    approvalFolders: [],
  };
}

function groupBriefsByProject(
  rows: TrackerRowDto[],
  userById: Map<string, BoardUser>,
): ProjectBriefsGroup[] {
  const groups = new Map<string, ProjectBriefsGroup>();
  for (const row of rows) {
    const brief = briefFromRow(row, userById);
    const group = groups.get(brief.projectId);
    if (group) {
      group.briefs.push(brief);
      continue;
    }
    groups.set(brief.projectId, {
      projectId: brief.projectId,
      platformProjectId: textField(row.fields, 'platformProjectId'),
      projectTitle: textField(row.fields, 'projectTitle') || null,
      briefs: [brief],
    });
  }
  const ordered = [...groups.values()];
  for (const group of ordered) {
    group.briefs.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }
  return ordered.sort((a, b) => (a.projectTitle ?? '').localeCompare(b.projectTitle ?? ''));
}

/**
 * Every brief of the board, all pages of the rows endpoint, grouped by project.
 * The query key is the one the optimistic brief mutations patch.
 */
export const boardBriefsQueryOptions = (params: BoardBriefsParams) =>
  queryOptions({
    queryKey: BRIEFS_QUERY_KEYS.allByWorkspace(params.workspaceId),
    queryFn: async ({ signal }): Promise<ProjectBriefsGroup[]> => {
      const rows: TrackerRowDto[] = [];
      const users: ListTrackerRowsResponseDto['users'] = [];
      let cursor: string | undefined;
      do {
        const response = await TrackersService.listRows({
          client: workspaceHttp,
          path: { trackerId: params.trackerId },
          query: {
            pageSize: BOARD_BRIEFS_PAGE_SIZE,
            ...(cursor ? { cursor } : {}),
          },
          signal,
        });
        if (!response.data) throw new Error('briefs rows returned an empty body');
        rows.push(...response.data.data);
        users.push(...response.data.users);
        cursor = response.data.hasMore ? (response.data.nextCursor ?? undefined) : undefined;
      } while (cursor);
      return groupBriefsByProject(rows, new Map(users.map((user) => [user.id, user])));
    },
    enabled: Boolean(params.workspaceId) && Boolean(params.trackerId),
  });
